import { FieldDef, GroupDef, GuiDef, ValidatorDef, TabDefinition, CollapsableDef } from '@/objects/gui';
import { FISDataRace } from '@/objects/generated';
import { markRaw, computed } from 'vue';
import {
    DataSourceLookup,
    InputText,
    ValidatedText,
    InputNullableFloat,
    ValidatedFloat,
    InputNullableInt,
    ValidatedInt,
} from '@/components';
import { useEventCommonStore } from '@/stores';
import { storeToRefs } from 'pinia';
import type { SelectLine, SelectLineValueType } from '@/objects/SelectLine';

export class FISDataRaceGuiDef extends GuiDef<FISDataRace> {
    constructor() {
        super('FISDataRace');

        const EventCommon = storeToRefs(useEventCommonStore());
        const RacesSource = computed(() => {
            return EventCommon.RacesAsSelectLine.value;
        });

        const ID = new FieldDef<FISDataRace>();
        ID.Name = 'ID';
        ID.FieldName = 'ID';
        ID.DefinedFor = 'Both';
        ID.TranslationKey = 'fisdatarace.id';
        ID.Editable = false;
        ID.Filterable = false;
        ID.Sortable = true;
        ID.CssClasses = '';
        ID.value = (fisdatarace) => fisdatarace.ID;
        ID.setValue = () => {};
        // @ts-expect-error @ts-ignore
        ID.CellRenderer = markRaw(DataSourceLookup);
        ID.CellRendererProps = { datasource: { mapsource: RacesSource }, readonly: true, hideCaret: true };
        ID.ExportValueFormatter = (obj: FISDataRace) => {
            return RacesSource.value.get(obj.ID)?.text;
        };
        ID.Validator = null;
        ID.SortDirection = undefined;
        ID.cellClassRules = undefined;
        ID.MinWidth = undefined;
        ID.MaxWidth = undefined;
        ID.Tooltip = null;
        this.Fields.push(ID);

        const Code = new FieldDef<FISDataRace>();
        Code.Name = 'Code';
        Code.FieldName = 'Code';
        Code.DefinedFor = 'Both';
        Code.TranslationKey = 'fisdatarace.code';
        Code.Editable = true;
        Code.Filterable = true;
        Code.Sortable = true;
        Code.CssClasses = '';
        Code.value = (fisdatarace) => fisdatarace.Code;
        Code.setValue = (fisdatarace, value) => (fisdatarace.Code = value);
        Code.DetailEditor = markRaw(InputText);
        Code.DetailEditorProps = {};
        Code.CellEditor = markRaw(InputText);
        Code.CellEditorProps = {};
        Code.CellRenderer = markRaw(ValidatedText);
        Code.CellRendererProps = {};
        Code.Validator = null;
        Code.SortDirection = undefined;
        Code.cellClassRules = undefined;
        Code.MinWidth = undefined;
        Code.MaxWidth = undefined;
        Code.Tooltip = null;
        this.Fields.push(Code);

        const Distance = new FieldDef<FISDataRace>();
        Distance.Name = 'Distance';
        Distance.FieldName = 'Distance';
        Distance.DefinedFor = 'Both';
        Distance.TranslationKey = 'fisdatarace.distance';
        Distance.Editable = true;
        Distance.Filterable = true;
        Distance.Sortable = true;
        Distance.CssClasses = '';
        Distance.value = (fisdatarace) => fisdatarace.Distance;
        Distance.setValue = (fisdatarace, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            fisdatarace.Distance = value;
        };
        Distance.DetailEditor = markRaw(InputNullableFloat);
        Distance.DetailEditorProps = {};
        Distance.CellEditor = markRaw(InputNullableFloat);
        Distance.CellEditorProps = {};
        Distance.CellRenderer = markRaw(ValidatedFloat);
        Distance.CellRendererProps = {};
        Distance.Validator = null;
        Distance.SortDirection = undefined;
        Distance.cellClassRules = undefined;
        Distance.MinWidth = undefined;
        Distance.MaxWidth = undefined;
        Distance.Tooltip = null;
        this.Fields.push(Distance);

        const Fisdistanse = new FieldDef<FISDataRace>();
        Fisdistanse.Name = 'Fisdistanse';
        Fisdistanse.FieldName = 'Fisdistanse';
        Fisdistanse.DefinedFor = 'Both';
        Fisdistanse.TranslationKey = 'fisdatarace.fisdistanse';
        Fisdistanse.Editable = true;
        Fisdistanse.Filterable = true;
        Fisdistanse.Sortable = true;
        Fisdistanse.CssClasses = '';
        Fisdistanse.value = (fisdatarace) => fisdatarace.Fisdistanse;
        Fisdistanse.setValue = (fisdatarace, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            fisdatarace.Fisdistanse = value;
        };
        Fisdistanse.DetailEditor = markRaw(InputNullableFloat);
        Fisdistanse.DetailEditorProps = {};
        Fisdistanse.CellEditor = markRaw(InputNullableFloat);
        Fisdistanse.CellEditorProps = {};
        Fisdistanse.CellRenderer = markRaw(ValidatedFloat);
        Fisdistanse.CellRendererProps = {};
        Fisdistanse.Validator = null;
        Fisdistanse.SortDirection = undefined;
        Fisdistanse.cellClassRules = undefined;
        Fisdistanse.MinWidth = undefined;
        Fisdistanse.MaxWidth = undefined;
        Fisdistanse.Tooltip = null;
        this.Fields.push(Fisdistanse);

        const Fisnavn = new FieldDef<FISDataRace>();
        Fisnavn.Name = 'Fisnavn';
        Fisnavn.FieldName = 'Fisnavn';
        Fisnavn.DefinedFor = 'Both';
        Fisnavn.TranslationKey = 'fisdatarace.fisnavn';
        Fisnavn.Editable = true;
        Fisnavn.Filterable = true;
        Fisnavn.Sortable = true;
        Fisnavn.CssClasses = '';
        Fisnavn.value = (fisdatarace) => fisdatarace.Fisnavn;
        Fisnavn.setValue = (fisdatarace, value) => (fisdatarace.Fisnavn = value);
        Fisnavn.DetailEditor = markRaw(InputText);
        Fisnavn.DetailEditorProps = {};
        Fisnavn.CellEditor = markRaw(InputText);
        Fisnavn.CellEditorProps = {};
        Fisnavn.CellRenderer = markRaw(ValidatedText);
        Fisnavn.CellRendererProps = {};
        Fisnavn.Validator = null;
        Fisnavn.SortDirection = undefined;
        Fisnavn.cellClassRules = undefined;
        Fisnavn.MinWidth = undefined;
        Fisnavn.MaxWidth = undefined;
        Fisnavn.Tooltip = null;
        this.Fields.push(Fisnavn);

        const Hd = new FieldDef<FISDataRace>();
        Hd.Name = 'Hd';
        Hd.FieldName = 'Hd';
        Hd.DefinedFor = 'Both';
        Hd.TranslationKey = 'fisdatarace.hd';
        Hd.Editable = true;
        Hd.Filterable = false;
        Hd.Sortable = true;
        Hd.CssClasses = '';
        Hd.value = (fisdatarace) => fisdatarace.Hd;
        Hd.setValue = (fisdatarace, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            fisdatarace.Hd = value;
        };
        Hd.DetailEditor = markRaw(InputNullableInt);
        Hd.DetailEditorProps = {};
        Hd.CellEditor = markRaw(InputNullableInt);
        Hd.CellEditorProps = {};
        Hd.CellRenderer = markRaw(ValidatedInt);
        Hd.CellRendererProps = {};
        Hd.Validator = null;
        Hd.SortDirection = undefined;
        Hd.cellClassRules = undefined;
        Hd.MinWidth = undefined;
        Hd.MaxWidth = undefined;
        Hd.Tooltip = null;
        this.Fields.push(Hd);

        const Mm = new FieldDef<FISDataRace>();
        Mm.Name = 'Mm';
        Mm.FieldName = 'Mm';
        Mm.DefinedFor = 'Both';
        Mm.TranslationKey = 'fisdatarace.mm';
        Mm.Editable = true;
        Mm.Filterable = false;
        Mm.Sortable = true;
        Mm.CssClasses = '';
        Mm.value = (fisdatarace) => fisdatarace.Mm;
        Mm.setValue = (fisdatarace, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            fisdatarace.Mm = value;
        };
        Mm.DetailEditor = markRaw(InputNullableInt);
        Mm.DetailEditorProps = {};
        Mm.CellEditor = markRaw(InputNullableInt);
        Mm.CellEditorProps = {};
        Mm.CellRenderer = markRaw(ValidatedInt);
        Mm.CellRendererProps = {};
        Mm.Validator = null;
        Mm.SortDirection = undefined;
        Mm.cellClassRules = undefined;
        Mm.MinWidth = undefined;
        Mm.MaxWidth = undefined;
        Mm.Tooltip = null;
        this.Fields.push(Mm);

        const Name = new FieldDef<FISDataRace>();
        Name.Name = 'Name';
        Name.FieldName = 'Name';
        Name.DefinedFor = 'Both';
        Name.TranslationKey = 'fisdatarace.name';
        Name.Editable = true;
        Name.Filterable = true;
        Name.Sortable = true;
        Name.CssClasses = '';
        Name.value = (fisdatarace) => fisdatarace.Name;
        Name.setValue = (fisdatarace, value) => (fisdatarace.Name = value);
        Name.DetailEditor = markRaw(InputText);
        Name.DetailEditorProps = {};
        Name.CellEditor = markRaw(InputText);
        Name.CellEditorProps = {};
        Name.CellRenderer = markRaw(ValidatedText);
        Name.CellRendererProps = {};
        Name.Validator = null;
        Name.SortDirection = undefined;
        Name.cellClassRules = undefined;
        Name.MinWidth = undefined;
        Name.MaxWidth = undefined;
        Name.Tooltip = null;
        this.Fields.push(Name);

        const Rundelengde = new FieldDef<FISDataRace>();
        Rundelengde.Name = 'Rundelengde';
        Rundelengde.FieldName = 'Rundelengde';
        Rundelengde.DefinedFor = 'Both';
        Rundelengde.TranslationKey = 'fisdatarace.rundelengde';
        Rundelengde.Editable = true;
        Rundelengde.Filterable = true;
        Rundelengde.Sortable = true;
        Rundelengde.CssClasses = '';
        Rundelengde.value = (fisdatarace) => fisdatarace.Rundelengde;
        Rundelengde.setValue = (fisdatarace, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            fisdatarace.Rundelengde = value;
        };
        Rundelengde.DetailEditor = markRaw(InputNullableFloat);
        Rundelengde.DetailEditorProps = {};
        Rundelengde.CellEditor = markRaw(InputNullableFloat);
        Rundelengde.CellEditorProps = {};
        Rundelengde.CellRenderer = markRaw(ValidatedFloat);
        Rundelengde.CellRendererProps = {};
        Rundelengde.Validator = null;
        Rundelengde.SortDirection = undefined;
        Rundelengde.cellClassRules = undefined;
        Rundelengde.MinWidth = undefined;
        Rundelengde.MaxWidth = undefined;
        Rundelengde.Tooltip = null;
        this.Fields.push(Rundelengde);

        const Runder = new FieldDef<FISDataRace>();
        Runder.Name = 'Runder';
        Runder.FieldName = 'Runder';
        Runder.DefinedFor = 'Both';
        Runder.TranslationKey = 'fisdatarace.runder';
        Runder.Editable = true;
        Runder.Filterable = false;
        Runder.Sortable = true;
        Runder.CssClasses = '';
        Runder.value = (fisdatarace) => fisdatarace.Runder;
        Runder.setValue = (fisdatarace, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            fisdatarace.Runder = value;
        };
        Runder.DetailEditor = markRaw(InputNullableInt);
        Runder.DetailEditorProps = {};
        Runder.CellEditor = markRaw(InputNullableInt);
        Runder.CellEditorProps = {};
        Runder.CellRenderer = markRaw(ValidatedInt);
        Runder.CellRendererProps = {};
        Runder.Validator = null;
        Runder.SortDirection = undefined;
        Runder.cellClassRules = undefined;
        Runder.MinWidth = undefined;
        Runder.MaxWidth = undefined;
        Runder.Tooltip = null;
        this.Fields.push(Runder);

        const Tc = new FieldDef<FISDataRace>();
        Tc.Name = 'Tc';
        Tc.FieldName = 'Tc';
        Tc.DefinedFor = 'Both';
        Tc.TranslationKey = 'fisdatarace.tc';
        Tc.Editable = true;
        Tc.Filterable = false;
        Tc.Sortable = true;
        Tc.CssClasses = '';
        Tc.value = (fisdatarace) => fisdatarace.Tc;
        Tc.setValue = (fisdatarace, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            fisdatarace.Tc = value;
        };
        Tc.DetailEditor = markRaw(InputNullableInt);
        Tc.DetailEditorProps = {};
        Tc.CellEditor = markRaw(InputNullableInt);
        Tc.CellEditorProps = {};
        Tc.CellRenderer = markRaw(ValidatedInt);
        Tc.CellRendererProps = {};
        Tc.Validator = null;
        Tc.SortDirection = undefined;
        Tc.cellClassRules = undefined;
        Tc.MinWidth = undefined;
        Tc.MaxWidth = undefined;
        Tc.Tooltip = null;
        this.Fields.push(Tc);

        const Kommentar = new FieldDef<FISDataRace>();
        Kommentar.Name = 'Kommentar';
        Kommentar.FieldName = 'Kommentar';
        Kommentar.DefinedFor = 'Both';
        Kommentar.TranslationKey = 'fisdatarace.kommentar';
        Kommentar.Editable = true;
        Kommentar.Filterable = true;
        Kommentar.Sortable = true;
        Kommentar.CssClasses = '';
        Kommentar.value = (fisdatarace) => fisdatarace.Kommentar;
        Kommentar.setValue = (fisdatarace, value) => (fisdatarace.Kommentar = value);
        Kommentar.DetailEditor = markRaw(InputText);
        Kommentar.DetailEditorProps = {};
        Kommentar.CellEditor = markRaw(InputText);
        Kommentar.CellEditorProps = {};
        Kommentar.CellRenderer = markRaw(ValidatedText);
        Kommentar.CellRendererProps = {};
        Kommentar.Validator = null;
        Kommentar.SortDirection = undefined;
        Kommentar.cellClassRules = undefined;
        Kommentar.MinWidth = undefined;
        Kommentar.MaxWidth = undefined;
        Kommentar.Tooltip = null;
        this.Fields.push(Kommentar);
    }

    public async LoadDependencies() {
        await useEventCommonStore().EnsureLoaded(['Races']);
    }
}
